import get from 'lodash/get';

import { Product } from 'redux/models';

const SOURCE_UNKNOWN = 0;
const SOURCE_STUDIO = 1;
const SOURCE_SHOP = 2;

const AVAILABILITY_GROUPINGS = {
  IN_STOCK: [-3, -2, -1],
  BACKORDERED: [-9, -8, -7, -4],
  MADE_TO_ORDER: [-6],
  OUT_OF_STOCK: [-11, -10, -5, 0]
};

/**
 * Shopping Cart model.
 * Tracks the items currently in the users cart.
 * Currently limited to products.
 */
class ShoppingCart {
  static defaultProps = {
    // All of the pricing data from server.
    // Updated with every cart update.
    // This data is pre-formatted as display strings.
    priceData: null,

    // Array of ProductItem's in the cart.
    products: []
  }

  /**
   * A default empty Cart for use when the application has not
   * yet received data from the server.
   */
  static defaultEmptyCart = new ShoppingCart({});

  /** Creates a new cart instance with defaults and optional overrides. */
  constructor (overrides) {
    this.fields = { ...ShoppingCart.defaultProps, ...overrides };
  }

  /** Creates a new cart copied from this with optional overrides. */
  makeCopy (overrides) {
    return new ShoppingCart({ ...this.fields, ...overrides });
  }

  /** Creates a new cart from the warehouse service response. */
  static createShoppingCartFromWarehouseResponse (whCart) {
    const { displayedInfo, orderItems } = whCart;

    let itemsBackordered = [];
    let itemsMadeToOrder = [];
    let itemsInStock = [];
    let itemsOutOfStock = [];

    const products = orderItems.map(whItem => {
      if (!whItem.productId) { return null; }

      const whProduct = get(whItem, 'product', {});
      if (!whProduct) { return null; }

      const designId = get(whItem, 'extra.product.designId', null);
      const productHotspotId = get(whItem, 'extra.product_hotspot_id', null);

      // The new property is 'purchase_flow' but we need to
      // continue supporting 'source' for BW compatibility.
      let whSource = get(whItem, 'extra.source', SOURCE_UNKNOWN);
      if (whSource === SOURCE_UNKNOWN) {
        whSource = get(whItem, 'extra.purchase_flow', SOURCE_UNKNOWN);
      }

      const product = new ProductItem({
        designId,
        itemId: whItem.id,
        itemType: whItem.type,
        productHotspotId,
        quantity: whItem.qty,
        subtotal: whItem.subtotal,
        metadata: whItem.metadata,
        source: whSource,
        product: Product.createProductFromWarehouseProductResponse({ whProduct })
      });

      const availability = whProduct.availability;
      switch (true) {
        case (AVAILABILITY_GROUPINGS.OUT_OF_STOCK.includes(availability)):
          itemsOutOfStock.push(product);
          break;
        case (AVAILABILITY_GROUPINGS.MADE_TO_ORDER.includes(availability)):
          itemsMadeToOrder.push(product);
          break;
        case (AVAILABILITY_GROUPINGS.BACKORDERED.includes(availability)):
          itemsBackordered.push(product);
          break;
        default:
          itemsInStock.push(product);
          break;
      }

      return product;
    }).filter(item => !!item);

    return new ShoppingCart({
      // Pricing metadata
      priceData: {
        calculatedFeesAndTax: displayedInfo.calculatedFeesAndTax,
        credit: displayedInfo.displayedCredit,
        currency: displayedInfo.displayedCurrency,
        discount: displayedInfo.displayedDiscount, // equals credit + promo
        promo: displayedInfo.displayedPromo,
        shipping: displayedInfo.displayedShipping,
        subtotal: displayedInfo.displayedSubtotal,
        tax: displayedInfo.displayedTax,
        serviceFee: displayedInfo.displayedFee,
        tieredDiscount: displayedInfo.displayedTieredDiscount,
        total: displayedInfo.displayedTotal
      },

      // Need to keep this around to pass
      // back to warehouse in place_order calls.
      displayedInfo,

      itemsBackordered,
      itemsMadeToOrder,
      itemsInStock,
      itemsOutOfStock,

      // Cart Items.
      // We only handle Products right now.
      products

    });
  }

  // ========
  // MUTATORS
  // ========

  /** Adds a Product to the cart. Returns a new instance.
   * If product already exists in cart will just update the quantity.
   */
  addProduct ({ product, quantity, designId, productHotspotId }) {
    const idToAdd = product.id();

    let updated = false;
    let products = this.fields.products.map(item => {
      if (item.product().id() === idToAdd) {
        updated = true;
        return item.setQuantity(quantity);
      }
      return item;
    });

    if (!updated) {
      products.push(ProductItem.makeProductItem({
        product,
        productHotspotId,
        quantity,
        designId,
        source: designId ? SOURCE_STUDIO : SOURCE_SHOP
      }));
    }

    return this.makeCopy({ products });
  }

  /** Deletes a Product form the cart. Returns a new instance. */
  deleteProduct (product) {
    const idToDelete = product.id();
    const products = this.fields.products.filter(item => item.product().id() !== idToDelete);

    return this.makeCopy({ products });
  }

  /** Updates the quantity for a product. Returns a new instance. */
  updateProductQuantity (product, quantity) {
    const idToUpdate = product.id();
    const products = this.fields.products.map(item => {
      return item.product().id() === idToUpdate
        ? item.setQuantity(quantity)
        : item;
    });

    return this.makeCopy({ products });
  }

  // =======
  // GETTERS
  // =======

  /** Returns the total number of items in the cart including quantity per item. */
  totalItemCount () {
    const totalItems = this.fields.products.reduce((sum, item) => {
      sum += item.quantity();
      return sum;
    }, 0);

    return totalItems;
  }

  /** Returns true if cart is empty */
  isEmpty () {
    return this.fields.products.length === 0;
  }

  /** Returns the number of unique items in the cart. */
  uniqueItemCount () {
    return this.fields.products.length;
  }

  /** Returns the current ProductItems in the cart. */
  products () {
    return this.fields.products;
  }

  /** Returns true if the cart includes the provided product. */
  isProductInCart (product) {
    return !!this.getProductItemFor(product);
  }

  /** Returns the ProductItem if the Product is in the cart or undefined. */
  getProductItemFor (product) {
    const idToFind = product.id();
    const prod = this.fields.products.find(item => item.product().id() === idToFind);
    return prod;
  }

  /**
   * Internal function for generating formatted locale strings for pricing data.
   * We currently only support USD.
   */
  _formattedCurrency (field) {
    const fieldValue = parseFloat(get(this.fields.priceData, field, 0));
    return fieldValue.toLocaleString(
      'en-US',
      { style: 'currency', currency: 'USD' }
    );
  }

  /** Returns the subtotal as a formatted string suitable for display */
  subtotal () {
    return this._formattedCurrency('subtotal');
  }

  subtotalNum () {
    return parseFloat(get(this.fields.priceData, 'subtotal', 0));
  }

  /** Returns the credit as a formatted string suitable for display */
  credit () {
    return this._formattedCurrency('credit');
  }

  /** Return true if the cart pricing metadata includes a credit. */
  hasCredit () {
    return parseFloat(get(this.fields.priceData, 'credit', 0)) > 0;
  }

  /** Returns the promocode credit as a formatted string suitable for display */
  promo () {
    return this._formattedCurrency('promo');
  }

  /** Return true if the cart pricing metadata includes a promocode credit. */
  hasPromo () {
    return parseFloat(get(this.fields.priceData, 'promo', 0)) > 0;
  }

  /**
   * DEPRECATED
   * Returns the discount as a formatted string suitable for display.
   * This expected value from BE is the sum of credit + promo.
   */
  discount () {
    return this._formattedCurrency('discount');
  }

  /**
   * DEPRECATED
   * Return true if the cart pricing metadata includes a discount.
   */
  hasDiscount () {
    return parseFloat(get(this.fields.priceData, 'discount', 0)) > 0;
  }

  /** Returns the tiered discount as a formatted string suitable for display */
  tieredDiscount () {
    return this._formattedCurrency('tieredDiscount');
  }

  hasCalculatedFeesAndTax () {
    return get(this.fields.priceData, 'calculatedFeesAndTax', false);
  }

  /** Return true if the cart pricing metadata includes a tiered discount. */
  hasTieredDiscount () {
    return parseFloat(get(this.fields.priceData, 'tieredDiscount', 0)) > 0;
  }

  /** Returns the shipping as a formatted string suitable for display */
  shipping () {
    return this._formattedCurrency('shipping');
  }

  /** Return true if the cart pricing metadata includes a non-zero shipping amount. */
  hasShipping () {
    return parseFloat(get(this.fields.priceData, 'shipping', 0)) > 0;
  }

  /** Returns the total as a formatted string suitable for display */
  total () {
    return this._formattedCurrency('total');
  }

  /** Return true if the cart pricing metadata includes estimated total. */
  hasTotal () {
    return parseFloat(get(this.fields.priceData, 'total', 0)) > 0;
  }

  /** Returns the tax as a formatted string suitable for display */
  tax () {
    return this._formattedCurrency('tax');
  }

  /** Return true if the cart pricing metadata includes tax. */
  hasTax () {
    return parseFloat(get(this.fields.priceData, 'tax', 0)) > 0;
  }

  /** Returns the sum of taxes and fees as a formatted string suitable for display */
  taxAndFees () {
    const tax = parseFloat(get(this.fields.priceData, 'tax', 0));
    const fee = parseFloat(get(this.fields.priceData, 'serviceFee', 0));
    return (tax + fee).toLocaleString(
      'en-US',
      { style: 'currency', currency: 'USD' }
    );
  }

  fee () {
    return this._formattedCurrency('serviceFee');
  }

  /** Return true if the cart pricing metadata includes serviceFee. */
  hasFee () {
    return parseFloat(get(this.fields.priceData, 'serviceFee', 0)) > 0;
  }

  /** Returns the pricing metadata currency string. */
  currency () {
    return this.fields.priceData.currency;
  }

  /** Returns the total savings as number. */
  totalSavings () {
    let savings = 0;
    savings += parseFloat(get(this.fields, 'priceData.promo', 0));
    savings += parseFloat(get(this.fields, 'priceData.tieredDiscount', 0));

    this.products().forEach(item => {
      const prod = item.product();
      savings += prod.discountAmount();
    });

    return savings;
  }

  /** Returns true if the total savings is > 0 */
  hasTotalSavings () {
    return this.totalSavings > 0;
  }

  /** Returns the total savings as a formatted string suitable for display */
  totalSavingsFormatted () {
    return this.totalSavings().toLocaleString(
      'en-US',
      { style: 'currency', currency: 'USD' }
    );
  }

  /** Returns the total order amount as a number. */
  totalOrderAmount () {
    return parseFloat(get(this.fields, 'priceData.total', 0));
  }

  /** Returns an orderItems array suitable for sending to update cart warehouse API. */
  toOrderItems () {
    return this.products().map(item => item.toOrderItem());
  }

  /** Returns displayedInfo suitable for place order warehouse calls. */
  toDisplayedInfo () {
    return this.fields.displayedInfo;
  }
}

/**
 * Product Item model.
 * This closely matches server response as cart updates
 * currently require sending back full cart with updates.
 */
class ProductItem {
  static ITEM_TYPE_PRODUCT = 26;

  static defaultProps = {
    designId: null,
    productHotspotId: null,
    itemId: null,
    itemType: ProductItem.ITEM_TYPE_PRODUCT,
    metadata: {},
    product: null,
    quantity: 0,
    source: SOURCE_UNKNOWN,
    subtotal: 0
  }

  /** Creates a new ProductItem instance with defaults and optional overrides. */
  constructor (overrides) {
    this.fields = { ...ProductItem.defaultProps, ...overrides };
  }

  /** Creates a new ProductItem copied from this with optional overrides. */
  makeCopy (overrides) {
    return new ProductItem({ ...this.fields, ...overrides });
  }

  /** Creates a new ProductItem from a product instance.  */
  static makeProductItem ({ product, quantity, designId, source, productHotspotId }) {
    return new ProductItem({
      designId,
      productHotspotId,
      product,
      quantity,
      source,
      subtotal: product.currentPrice()
    });
  }

  /** Sets the item quantity. Returns a new instance. */
  setQuantity (quantity) {
    return this.makeCopy({ quantity });
  }

  // =======
  // GETTERS
  // =======

  /** Returns the item id. */
  itemId () {
    return this.fields.itemId;
  }

  /** Returns the item type. */
  type () {
    return this.fields.itemType;
  }

  /** Returns the item quantity. */
  quantity () {
    return this.fields.quantity;
  }

  /** Returns the item subtotal. */
  subtotal () {
    return this.fields.subtotal;
  }

  /** Returns the product instance. */
  product () {
    return this.fields.product;
  }

  /** Returns ID for hotspot if available */
  productHotspotId () {
    return this.fields.productHotspotId;
  }

  metadata () {
    return this.fields.metadata;
  }

  isFreeShippingIneligible () {
    return this.metadata().freeShippingIneligible;
  }

  /** Returns an orderItem suitable for sending to update cart warehouse API. */
  toOrderItem () {
    let whProd = {};
    const prod = this.product().warehouseProduct();

    // Convert important keys from camelCase to snake_case
    Object.keys(prod).forEach(key => {
      switch (key) {
        case 'id':
          whProd['id'] = prod['id'];
          break;
        case 'images':
          whProd['images'] = prod['images'];
          break;
        case 'name':
          whProd['name'] = prod['name'];
          break;
        case 'source':
          whProd['source'] = prod['source'];
          break;
        case 'quantityMultiple':
          if (prod['quantity_multiple'] === undefined) {
            whProd['quantity_multiple'] = prod['quantityMultiple'];
          }
          break;
        case 'quantity_multiple':
          whProd['quantity_multiple'] = prod['quantity_multiple'];
          break;
        // price data
        case 'currentPrice':
          if (prod['current_price'] === undefined) {
            whProd['current_price'] = prod['currentPrice'];
          }
          break;
        case 'current_price':
          whProd['current_price'] = prod['current_price'];
          break;
        case 'promoPrice':
          if (prod['promo_price'] === undefined) {
            whProd['promo_price'] = prod['promoPrice'];
            whProd['salePrice'] = prod['promoPrice'];
          }
          break;
        case 'promo_price':
          whProd['promo_price'] = prod['promo_price'];
          whProd['salePrice'] = prod['promo_price'];
          break;
        case 'price':
          whProd['price'] = prod['price'];
          whProd['regularPrice'] = prod['price'];
          break;
        default:
          break;
      }
    });

    let orderItem = {
      extra: {
        product: whProd,
        product_hotspot_id: this.productHotspotId()
      },
      productId: this.product().id(),
      qty: this.quantity(),
      subtotal: this.quantity() * this.product().currentPrice(),
      type: this.type()
    };

    if (this.itemId()) {
      orderItem.__id = this.itemId();
    }

    const designId = this.fields.designId;
    if (designId) {
      orderItem.extra.product.designId = designId;
    }

    orderItem.extra.purchase_flow = this.fields.source;

    return orderItem;
  }
}

export default ShoppingCart;
